import { enumKeys } from '@/utils/enums'
import { Vue, Component, Prop, Emit, Ref } from 'vue-property-decorator'
import { ScientificAnalysisSearchEntityType, ScientificAnalysisFilterEntityType, ScientificAnalysisSaveResponse, ScientificAnalysisTemplateHeader } from '../../services/scientific-analysis-dtos'
import {
    initialScientificAnalysisSearchPanelState,
    SCIENTIFIC_ANALYSIS_NONE_SELECTED_FILTER,
    ScientificAnalysisEntityFiltersVO,
    ScientificAnalysisSearchPanelState,
    SEARCH_EVENT_NAME
} from '@/modules/scientific-analysis-module/components/scientific-analysis-search-panel/scientific-analysis-search-panel.models'
import ScientificAnalysisSummaryDetail from '@/modules/scientific-analysis-module/components/scientific-analysis-summary-detail/scientific-analysis-summary-detail.vue'
import VtBubble from '@/components/common/VtBubble.vue'
import { ScientificAnalysis, ScientificAnalysisFilter, ScientificAnalysisEntityFilters, SCIENTIFIC_ANALYSIS_DEFAULT_GROUP_BY_PARENT_ENTITY_BEHAVIOR, ScientificAnalysisConditionValidationErrorCode } from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import { loadedFilterEntityTypes, scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import sessionStore from '@/session/sessionStore'
import { CommonLaboratoryResponse } from '@/api/services/common.dto'
import ScientificAnalysisSaveModal from '@/modules/scientific-analysis-module/components/scientific-analysis-save-modal/scientific-analysis-save-modal.vue'
import ScientificAnalysisSaveModalComponent from '@/modules/scientific-analysis-module/components/scientific-analysis-save-modal/scientific-analysis-save-modal'
import ScientificAnalysisRenameModal from '@/modules/scientific-analysis-module/components/scientific-analysis-rename-modal/scientific-analysis-rename-modal.vue'
import ScientificAnalysisRenameModalComponent from '@/modules/scientific-analysis-module/components/scientific-analysis-rename-modal/scientific-analysis-rename-modal'
import ScientificAnalysisImportModal from '@/modules/scientific-analysis-module/components/scientific-analysis-import-modal/scientific-analysis-import-modal.vue'
import ScientificAnalysisDetailModal from '@/modules/scientific-analysis-module/components/scientific-analysis-detail-modal/scientific-analysis-detail-modal.vue'
import ScientificAnalysisSearchTabTemplates from '@/modules/scientific-analysis-module/components/scientific-analysis-search-tab-templates/scientific-analysis-search-tab-templates.vue'
import ScientificAnalysisSearchTabTemplatesComponent from '@/modules/scientific-analysis-module/components/scientific-analysis-search-tab-templates/scientific-analysis-search-tab-templates'
import { BPopover, BTabs } from 'bootstrap-vue'
import i18n from '@/lang/i18n'
import UserappOperationConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappOperationConfigurationServices'
import { ModuleOperation } from '@/api/operations/models/module-operation'

@Component({
    components: {
        VtBubble,
        ScientificAnalysisSearchTabTemplates,
        ScientificAnalysisSummaryDetail,
        ScientificAnalysisSaveModal,
        ScientificAnalysisRenameModal,
        ScientificAnalysisDetailModal,
        ScientificAnalysisImportModal
    }
})
export default class ScientificAnalysisSearchPanel extends Vue {
    @Prop() scientificAnalysis!: ScientificAnalysis
    @Ref('scientificAnalysisSaveModal') readonly scientificAnalysisSaveModal!: ScientificAnalysisSaveModalComponent
    @Ref('scientificAnalysisRenameModal') readonly scientificAnalysisRenameModal!: ScientificAnalysisRenameModalComponent
    @Ref('scientificAnalysisSearchTab') readonly scientificAnalysisSearchTab!: BTabs
    @Ref('scientificAnalysisSearchTabTemplates') readonly scientificAnalysisSearchTabTemplates!: ScientificAnalysisSearchTabTemplatesComponent
    @Ref('savedSearchPopTitle') readonly savedSearchPopTitle!:BPopover
    state: ScientificAnalysisSearchPanelState
    service = scientificAnalysisService
    sessionStore = sessionStore
    userAppServices = UserappOperationConfigurationServices

    constructor() {
        super()
        this.state = initialScientificAnalysisSearchPanelState()
        for (const value of enumKeys(ScientificAnalysisSearchEntityType)) {
            this.state.searchEntities.push(
                ScientificAnalysisSearchEntityType[value]
            )
        }
        for (const value of enumKeys(ScientificAnalysisFilterEntityType)) {
            const scientificAnalysisFilterEntityType = ScientificAnalysisFilterEntityType[value]
            this.loadMetadataForFilterEntity(scientificAnalysisFilterEntityType)
            this.state.filterEntities.push(scientificAnalysisFilterEntityType)
        }
    }

    async mounted():Promise<void> {
        await this._updateLaboratories()
    }

    private async loadMetadataForFilterEntity(scientificAnalysisFilterEntityType: ScientificAnalysisFilterEntityType): Promise<void> {
        const scientificAnalysisMetadataEntityFilterConditionsResponse = await scientificAnalysisService.getMetadata(scientificAnalysisFilterEntityType)
        loadedFilterEntityTypes[scientificAnalysisFilterEntityType] = scientificAnalysisMetadataEntityFilterConditionsResponse
    }

    async _updateLaboratories(): Promise<void> {
        if (this.scientificAnalysis.laboratoryIds.length === 0) {
            this.scientificAnalysis.laboratoryIds.push(this.$store.getters.labId)
        }

        if (this.$store.getters.userInformation) {
            const permissions = await this.userAppServices.getUserappOperationsByUserappId(this.$store.getters.userInformation.id)
            const filteredPermissions = permissions.filter((permission: any) => permission.operation.code === ModuleOperation.SCIENTIFIC_PANEL_ACCESS)
            this.state.laboratories = filteredPermissions.map((permission: any) => {
                return {
                    labId: permission.laboratory.labId,
                    labName: permission.laboratory.labName
                }
            })
        }
    }

    get _disableSearchButton(): boolean {
        return this._isEmptyAnalysis() || this.hasAnyValidationError()
    }

    get selectedLaboratories(): CommonLaboratoryResponse[] {
        const selectedLabs: CommonLaboratoryResponse[] = []
        this.scientificAnalysis.laboratoryIds.forEach(id => {
            let labFound: CommonLaboratoryResponse | undefined = this.state.laboratories.find(lab => lab.labId === id)
            if (labFound) {
                selectedLabs.push(labFound)
            }
        })
        return selectedLabs
    }

    get _showSavedSearch(): boolean {
        return this.state.loadedAnalysisDTO !== null
    }

    onSelectLaboratory(labs: CommonLaboratoryResponse[]): void {
        this.scientificAnalysis.laboratoryIds = []
        labs.forEach(lab => {
            if (lab.labId) { this.scientificAnalysis.laboratoryIds.push(lab.labId) }
        })
        this._onFilterModification()
    }

    showGroupedByParentEntity(scientificAnalysisEntityFilter: ScientificAnalysisEntityFilters): boolean {
        const scientificAnalysisMetadataEntityFilterConditionsResponse = loadedFilterEntityTypes[scientificAnalysisEntityFilter.entityType]
        if (scientificAnalysisMetadataEntityFilterConditionsResponse) {
            return scientificAnalysisMetadataEntityFilterConditionsResponse.groupedByParent
        }
        return false
    }

    public async removeFilter(entityFilters: ScientificAnalysisEntityFilters, indexFilter: number): Promise<void> {
        const response = await this.$_showConfirmationModal(this.$t('scientificAnalysis.filterForm.remove.confirmationTitle') as string,
            this.$t('scientificAnalysis.filterForm.remove.confirmationOk') as string,
            this.$t('scientificAnalysis.filterForm.remove.confirmationCancel') as string)
        if (response) {
            entityFilters.filters.splice(indexFilter, 1)
            const indexEntityFilters = this.scientificAnalysis.entityFilters.findIndex(fg => fg.entityType === entityFilters.entityType)
            const entityFiltersVO = this.state.entityFilters[indexEntityFilters]
            if (entityFilters.filters.length === 0) {
                this.scientificAnalysis.entityFilters.splice(indexEntityFilters, 1)
                this.state.entityFilters.splice(indexEntityFilters, 1)
            }

            if (!entityFiltersVO || entityFiltersVO.selectedFilter === indexFilter) {
                entityFiltersVO.selectedFilter = SCIENTIFIC_ANALYSIS_NONE_SELECTED_FILTER
                this._emitOnFilterDeselectedEvent()
            } else if (entityFiltersVO.selectedFilter > indexFilter) {
                entityFiltersVO.selectedFilter--
                this._emitOnFilterSelectedEvent(entityFilters, entityFilters.filters[entityFiltersVO.selectedFilter], entityFiltersVO.selectedFilter)
            }
            this._onFilterModification()
        }
    }

    public unselectAllFilters(): void {
        this.state.entityFilters.forEach(fg => { fg.selectedFilter = SCIENTIFIC_ANALYSIS_NONE_SELECTED_FILTER })
    }

    get _showEmptyFilters(): boolean {
        return this.scientificAnalysis.entityFilters.length === 0
    }

    _getLocalizedEntityName(entity: ScientificAnalysisSearchEntityType | ScientificAnalysisFilterEntityType): string {
        return this.$t(`scientificAnalysis.entity.${entity}`) as string
    }

    _getLocalizedParentEntityFilterLabel(entity: ScientificAnalysisSearchEntityType | ScientificAnalysisFilterEntityType): string {
        if (entity === ScientificAnalysisFilterEntityType.CASE) {
            return this.$tc(`scientificAnalysis.sidebar.caseFilterLabel`)
        } else if (entity === ScientificAnalysisFilterEntityType.SAMPLE) {
            return this.$tc(`scientificAnalysis.sidebar.sampleFilterLabel`)
        } else if (entity === ScientificAnalysisFilterEntityType.PORTION) {
            return this.$tc(`scientificAnalysis.sidebar.portionFilterLabel`)
        } else if (entity === ScientificAnalysisFilterEntityType.SUBPORTION) {
            return this.$tc(`scientificAnalysis.sidebar.subportionFilterLabel`)
        } else if (entity === ScientificAnalysisFilterEntityType.REQUEST) {
            return this.$tc(`scientificAnalysis.sidebar.requestFilterLabel`)
        } else if (entity === ScientificAnalysisFilterEntityType.COMPLEMENTARY) {
            return this.$tc(`scientificAnalysis.sidebar.complementaryFilterLabel`)
        } else {
            return ''
        }
    }

    _addFilterToSelectedEntity(): void {
        if (this.state.selectedFilterEntity != null) {
            let entityFiltersVO: ScientificAnalysisEntityFiltersVO | undefined = this.state.entityFilters.find(
                ef => ef.dto.entityType === this.state.selectedFilterEntity
            )

            if (!entityFiltersVO) {
                const entityFilters: ScientificAnalysisEntityFilters = {
                    entityType: this.state.selectedFilterEntity,
                    groupedByParentEntity: SCIENTIFIC_ANALYSIS_DEFAULT_GROUP_BY_PARENT_ENTITY_BEHAVIOR,
                    filters: []
                }
                this.scientificAnalysis.entityFilters.push(entityFilters)

                entityFiltersVO = {
                    dto: entityFilters,
                    hide: false,
                    selectedFilter: SCIENTIFIC_ANALYSIS_NONE_SELECTED_FILTER
                }
                this.state.entityFilters.push(entityFiltersVO)
            }

            entityFiltersVO.dto.filters.push({
                fieldGroups: []
            })
            this._onFilterSelected(entityFiltersVO, entityFiltersVO.dto.filters.length - 1)
            this._onFilterModification()
        }
    }

    _isFilterSelected(entityFiltersVO: ScientificAnalysisEntityFiltersVO, index: number): boolean {
        return entityFiltersVO.selectedFilter === index
    }

    _onSelectSearchEntity(): void {
        this._onFilterModification()
    }

    private hasAnyValidationError(): boolean {
        return this.state.entityFilters.some(entityFiltersVO => entityFiltersVO.dto.filters.some(
            filter => filter.fieldGroups.some(
                fg => fg.conditions.some(
                    cond => cond.validationErrorCode !== ScientificAnalysisConditionValidationErrorCode.NONE))))
    }

    _hasValidationErrors(entityFiltersVO: ScientificAnalysisEntityFiltersVO, indexFilter:number): boolean {
        return entityFiltersVO.dto.filters[indexFilter].fieldGroups.some(
            fg => fg.conditions.some(
                cond => cond.validationErrorCode !== ScientificAnalysisConditionValidationErrorCode.NONE))
    }

    _onFilterSelected(entityFiltersVO: ScientificAnalysisEntityFiltersVO, index: number): void {
        this._selectFilter(entityFiltersVO, index)
        this._emitOnFilterSelectedEvent(entityFiltersVO.dto, entityFiltersVO.dto.filters[index], index)
    }

    @Emit('filterSelectedEvent')
    _emitOnFilterSelectedEvent(entityFilters: ScientificAnalysisEntityFilters, filter: ScientificAnalysisFilter, index: number): void {

    }

    @Emit('filterSelectedEvent')
    _emitOnFilterDeselectedEvent(): void {

    }

    _selectFilter(entityFiltersVO: ScientificAnalysisEntityFiltersVO, index: number): void {
        this.unselectAllFilters()
        if (entityFiltersVO.dto.filters.length > index) {
            entityFiltersVO.selectedFilter = index
        }
    }

    _toogleEntityFilters(entityFiltersVO: ScientificAnalysisEntityFiltersVO): void {
        entityFiltersVO.hide = !entityFiltersVO.hide
    }

    _clear(): void {
        this._emitOnFilterDeselectedEvent()
        this.state.entityFilters = []
        this.scientificAnalysis.entityFilters = []
        this.state.selectedFilterEntity = null
        this.scientificAnalysis.laboratoryIds = []
        this.scientificAnalysis.searchedEntityType = null
        this.state.loadedAnalysisDTO = null
        this._onFilterModification()
    }

    async _onRemoveFilter(event: MouseEvent, entityFiltersVO: ScientificAnalysisEntityFiltersVO, indexFilter: number): Promise<void> {
        event.stopPropagation()
        await this.removeFilter(entityFiltersVO.dto, indexFilter)
    }

    _isEmptyAnalysis(): boolean {
        if (this.scientificAnalysis.searchedEntityType !== null && this.scientificAnalysis.entityFilters.length > 0) {
            return this.scientificAnalysis.entityFilters.some(ef => {
                if (ef.filters.length > 0) {
                    return ef.filters.some(filter => filter.fieldGroups.length === 0)
                }
                return true
            })
        }
        return true
    }

    _onTemplateTabActivate(): void {
        this.scientificAnalysisSearchTabTemplates.update()
    }

    _search(): void {
        if (!this._isEmptyAnalysis()) {
            this.$emit(SEARCH_EVENT_NAME, this.scientificAnalysis)
        }
    }

    _getFilterIcon(filterEntityType: ScientificAnalysisFilterEntityType): string {
        return this.service.getFilterIcon(filterEntityType)
    }

    _getTotalFilters(filter: ScientificAnalysisFilter): number {
        return filter.fieldGroups.map(fg => fg.conditions.length).reduce((acc, length) => acc + length, 0)
    }

    @Emit('searchModification')
    _onFilterModification(): void { }

    _onSaveSearchClick(): void {
        this.scientificAnalysisSaveModal.show(this.scientificAnalysis, this.state.loadedAnalysisDTO)
    }

    async _onTemplateSaved(templateSaved: ScientificAnalysisTemplateHeader): Promise<void> {
        const template = await this.$_toCallSecureLoading(this.service.getTemplate(templateSaved.id))
        if (template) { this._onTemplateLoad(template) }
    }

    async _onTemplateDeleted(templateDeletedId: number):Promise<void> {
        if (this.state.loadedAnalysisDTO && templateDeletedId === this.state.loadedAnalysisDTO?.id) {
            this.state.loadedAnalysisDTO = null
        }
    }

    _onTemplateLoad(analysisDTO: ScientificAnalysisSaveResponse): void {
        // BTab is not typed by bootstrap library
        (this.scientificAnalysisSearchTab as any).activate()
        this.state.loadedAnalysisDTO = analysisDTO
        this.unselectAllFilters()
        this._emitOnFilterDeselectedEvent()
        const loadedScientificAnalysis = JSON.parse(analysisDTO.template) as ScientificAnalysis
        this.state.entityFilters = []
        loadedScientificAnalysis.entityFilters.forEach(entityFilter => {
            this.state.entityFilters.push(
                {
                    dto: entityFilter,
                    hide: true,
                    selectedFilter: SCIENTIFIC_ANALYSIS_NONE_SELECTED_FILTER
                }
            )
        })
        this.$emit('template-load', loadedScientificAnalysis)
    }

    async _onRemoveLoadedAnalysisClick(): Promise<void> {
        if (this.state.loadedAnalysisDTO) {
            const confirmation = await this.$_showConfirmationModal(i18n.tc('scientificAnalysis.sidebar.deleteQuestion'))
            if (confirmation) {
                this.$_updateLoading(true)
                const removed = await this.$_toCallSecureLoading(this.service.removeTemplate(this.state.loadedAnalysisDTO?.id))
                if (removed) {
                    this._clear()
                    this.$_showToastSuccess(i18n.tc('scientificAnalysis.sidebar.deleteSuccess'))
                    this.$_updateLoading(false)
                }
            }
        }
    }

    async _onExportTemplateClick(): Promise<void> {
        if (this.state.loadedAnalysisDTO) {
            this.$_updateLoading(true)
            this.savedSearchPopTitle.$emit('close')
            const exported = await this.$_toCallSecureLoading(this.service.exportTemplates([this.state.loadedAnalysisDTO.id]))
            if (exported) {
                this.$_updateLoading(false)
                this.$_showToastSuccess(i18n.tc('scientificAnalysis.sidebar.exportsuccess'))
            }
        }
    }

    _onRenameTemplateClick(): void {
        if (this.state.loadedAnalysisDTO) {
            this.scientificAnalysisRenameModal.show(this.state.loadedAnalysisDTO.id, this.state.loadedAnalysisDTO.name)
        }
    }
}
